import type { QuoteLineItem } from '@/lib/api/quotes';
import type { Translations } from '@/lib/i18n';

import type { LineItemRow } from './schema';
import type { Quote } from './types';

export const PAGE_SIZE = 9;
export const MAX_LINE_ITEMS = 100;

// ─── Line items ──────────────────────────────────────────────────────────────

// Sums the numeric `amount`s, ignoring blank / non-numeric rows. Accepts the
// string-y form rows or the API's numeric line items.
export function lineItemsTotal(rows: { amount: number | string }[]): number {
  return rows.reduce((sum, row) => {
    const n = typeof row.amount === 'number' ? row.amount : Number(row.amount);
    return Number.isFinite(n) ? sum + n : sum;
  }, 0);
}

// Form rows → API `QuoteLineItem[]`. Drops rows with neither a description nor
// an amount; coerces a blank amount to 0.
export function toLineItems(rows: LineItemRow[]): QuoteLineItem[] {
  return rows
    .map((row) => ({ description: row.description.trim(), amount: Number(row.amount) || 0 }))
    .filter((row) => row.description !== '' || row.amount !== 0);
}

// API line items → form rows (amount back to a string for the <input>).
export function fromLineItems(items: QuoteLineItem[]): LineItemRow[] {
  return items.map((item) => ({
    description: item.description,
    amount: String(item.amount),
  }));
}

// The API only has three real statuses (pending | accepted | declined). For a
// pending quote we surface where it is in the owner's workflow using the
// `sent_at` / `approved_at` timestamps.
export type QuotePhase = 'draft' | 'sent' | 'approved' | 'accepted' | 'declined';

export function quotePhase(quote: Quote): QuotePhase {
  if (quote.status === 'accepted') return 'accepted';
  if (quote.status === 'declined') return 'declined';
  if (quote.approved_at) return 'approved';
  if (quote.sent_at) return 'sent';
  return 'draft';
}

export function quotePhaseLabel(phase: QuotePhase, t: Translations): string {
  switch (phase) {
    case 'accepted':
      return t.quoteStatusAccepted;
    case 'declined':
      return t.quoteStatusDeclined;
    case 'approved':
      return t.quotePhaseApproved;
    case 'sent':
      return t.quotePhaseSent;
    default:
      return t.quotePhaseDraft;
  }
}

export function quotePhaseColorClass(phase: QuotePhase): string {
  switch (phase) {
    case 'accepted':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'declined':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    case 'approved':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'sent':
      return 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400';
    default:
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  }
}

// Quote timestamps are full ISO datetimes (unlike job `start_date`, which is a
// bare YYYY-MM-DD), so parse them straight through `Date`.
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
